import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, User, ArrowRight, RefreshCw, AlertTriangle } from 'lucide-react';
import { useSupabase } from '../contexts/SupabaseContext';
import { supabase } from '../lib/supabase';

const SellerLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useSupabase();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [captchaEnabled, setCaptchaEnabled] = useState(false);

  // Generate random captcha
  const generateCaptcha = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(result);
    setCaptchaInput('');
    setCaptchaError('');
  };

  // Check if all fields are completed
  const isFormComplete = formData.email.trim() !== '' && formData.password.trim() !== '';

  // Check captcha settings from localStorage
  useEffect(() => {
    const adminSettings = localStorage.getItem('admin_settings');
    if (adminSettings) {
      try {
        const settings = JSON.parse(adminSettings);
        setCaptchaEnabled(settings.enableCaptcha && settings.captchaOnLogin);
      } catch (err) {
        console.error('Error parsing admin settings:', err);
        setCaptchaEnabled(false);
      }
    } else {
      setCaptchaEnabled(false);
    }
  }, []);

  // Initialize captcha when form becomes complete
  useEffect(() => {
    if (isFormComplete && !captchaCode && captchaEnabled) {
      generateCaptcha();
    }
  }, [isFormComplete, captchaCode, captchaEnabled]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isFormComplete && captchaEnabled) {
      if (captchaInput !== captchaCode) {
        setCaptchaError('Captcha code is incorrect. Please try again.');
        generateCaptcha();
        return;
      }
    }
    
    setIsLoading(true);
    setLoginError('');
    
    try {
      // Sign in with Supabase
      const authResponse = await signIn(formData.email, formData.password);
      
      // Check if authResponse and user exist
      if (!authResponse || !authResponse.user) {
        throw new Error('Authentication failed - no user data received');
      }
      
      // Check if user is a seller/supplier
      let accountType = authResponse.user.user_metadata?.account_type;
      
      // If not found in metadata, try to get from users table
      if (!accountType) {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('account_type')
          .eq('email', formData.email)
          .maybeSingle();
        
        if (!userError && userData) {
          accountType = userData.account_type;
        }
      }
      
      // Check if user is a supplier
      if (accountType !== 'supplier') {
        setLoginError('Access denied. This login is for suppliers only. Please use the regular login for buyers.');
        setIsLoading(false);
        return;
      }
      
      // Redirect to seller central or the intended page
      const from = location.state?.from?.pathname || '/seller-central';
      navigate(from);
      
    } catch (error) {
      console.error('Error signing in:', error);
      setLoginError('Invalid email or password. Please try again.');
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setCaptchaError('');
    setLoginError('');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <img 
            src="/madeinbd-logo.png" 
            alt="Made in BD" 
            className="h-12 w-auto object-contain"
          />
        </Link>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Seller Login
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Access your seller dashboard
        </p>
        <p className="mt-1 text-center text-xs text-gray-500">
          For buyers, please use the{' '}
          <Link to="/signin" className="font-medium text-orange-600 hover:text-orange-500">
            regular login
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address *
              </label>
              <div className="mt-1 relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                  placeholder="Enter your email"
                />
                <Mail className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password *
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 pl-10 pr-10 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                  placeholder="Enter your password"
                />
                <Lock className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {loginError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2" />
                <span>{loginError}</span>
              </div>
            )}

            {/* Captcha - Only show when form is complete */}
            {isFormComplete && captchaEnabled && (
              <div className="animate-fade-in">
                <label htmlFor="captcha" className="block text-sm font-medium text-gray-700">
                  Security Verification *
                </label>
                <div className="mt-1 space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="bg-gray-100 px-4 py-2 rounded-md border border-gray-300 font-mono text-lg tracking-wider select-none">
                      {captchaCode}
                    </div>
                    <button
                      type="button"
                      onClick={generateCaptcha}
                      className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                      title="Refresh captcha"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                  </div>
                  <input
                    id="captcha"
                    name="captcha"
                    type="text"
                    required
                    value={captchaInput}
                    onChange={(e) => {
                      setCaptchaInput(e.target.value);
                      setCaptchaError('');
                    }}
                    className={`appearance-none block w-full px-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm ${
                      captchaError ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Enter the code above"
                  />
                  {captchaError && (
                    <p className="text-sm text-red-600">{captchaError}</p>
                  )}
                </div>
              </div>
            )}

            {/* Remember Me */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  name="rememberMe"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-900">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <Link to="/forgot-password" className="font-medium text-orange-600 hover:text-orange-500">
                  Forgot your password?
                </Link>
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading || !isFormComplete || (isFormComplete && captchaEnabled && captchaInput !== captchaCode)}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Sign in to Seller Central
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                )}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">New to selling?</span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Link 
                to="/create-account?accountType=supplier" 
                className="font-medium text-orange-600 hover:text-orange-500"
              >
                Create a supplier account
              </Link>
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-6">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-orange-800 mb-2">Seller Account Security</h3>
              <p className="text-xs text-orange-700">
                This login is exclusively for verified suppliers and sellers. Your account is protected with enhanced security measures including captcha verification.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerLogin;